import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  MapPin, Camera, Heart, Trash2, Plus, X, ChevronDown, ChevronUp,
  Car, Coffee, Bath, Mountain, Waves, Trees, Navigation, RefreshCw,
  Star, User, Clock, Filter, Phone, ExternalLink, Search, Map as MapIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import imageCompression from 'browser-image-compression';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Tipi di luogo
const PLACE_TYPES = [
  { id: 'spiaggia', label: 'Spiaggia', icon: Waves, color: 'text-cyan-400' },
  { id: 'sentiero', label: 'Sentiero', icon: Mountain, color: 'text-emerald-400' },
  { id: 'baia', label: 'Baia', icon: Waves, color: 'text-blue-400' },
  { id: 'scogliera', label: 'Scogliera', icon: Mountain, color: 'text-amber-400' },
  { id: 'riserva', label: 'Riserva Naturale', icon: Trees, color: 'text-green-400' },
  { id: 'altro', label: 'Altro', icon: MapPin, color: 'text-slate-400' }
];

// Livelli difficoltà
const DIFFICULTY_LEVELS = [
  { id: 'facile', label: 'Facile', color: 'bg-emerald-500', desc: 'Accessibile a tutti' },
  { id: 'medio', label: 'Medio', color: 'bg-amber-500', desc: 'Richiede un po\' di cammino' },
  { id: 'difficile', label: 'Difficile', color: 'bg-red-500', desc: 'Percorso impegnativo' }
];

const PlacesSection = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingLocation, setUpdatingLocation] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expandedPlace, setExpandedPlace] = useState(null);
  const [showIntroPopup, setShowIntroPopup] = useState(true);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapPickerLocation, setMapPickerLocation] = useState({ lat: 41.9028, lng: 12.4964 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [editingPlaceId, setEditingPlaceId] = useState(null); // Per aggiungere posizione a un luogo esistente
  
  const photoInputRef = useRef(null);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    place_type: 'spiaggia',
    difficulty: 'facile',
    has_parking: false,
    has_bathroom: false,
    has_food: false,
    phone: '',
    photo_url: null,
    location: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [compressingPhoto, setCompressingPhoto] = useState(false);

  // Fetch luoghi
  const fetchPlaces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/places`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaces(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento luoghi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  // Compressione foto
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCompressingPhoto(true);
    try {
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1024,
        useWebWorker: true
      };
      const compressedFile = await imageCompression(file, options);
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => ({ ...prev, photo_url: reader.result }));
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      toast.error('Errore compressione foto');
    } finally {
      setCompressingPhoto(false);
    }
  };

  // Ottieni posizione GPS
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('GPS non disponibile');
      return;
    }
    
    toast.info('Rilevamento posizione...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(prev => ({
          ...prev,
          location: { lat: pos.coords.latitude, lng: pos.coords.longitude }
        }));
        toast.success('Posizione rilevata!');
      },
      () => {
        toast.error('Impossibile ottenere posizione');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Cerca indirizzo e centra la mappa
  const searchAddress = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      // Usa Google Geocoding API
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchQuery)}&key=${MAPS_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setMapPickerLocation({ lat, lng });
        toast.success('Posizione trovata! Tocca la mappa per confermare.');
      } else {
        toast.error('Indirizzo non trovato');
      }
    } catch (error) {
      toast.error('Errore nella ricerca');
    } finally {
      setSearching(false);
    }
  };

  // Conferma posizione selezionata dalla mappa
  const confirmMapLocation = () => {
    if (editingPlaceId) {
      // Aggiorna un luogo esistente
      updatePlaceLocation(editingPlaceId, mapPickerLocation);
    } else {
      // Imposta nel form per nuovo luogo
      setForm(prev => ({ ...prev, location: mapPickerLocation }));
      toast.success('Posizione impostata!');
    }
    setShowMapPicker(false);
    setEditingPlaceId(null);
    setSearchQuery('');
  };

  // Aggiorna posizione di un luogo esistente
  const updatePlaceLocation = async (placeId, location) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/places/${placeId}/location`,
        location,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Posizione aggiornata!');
      fetchPlaces();
    } catch (error) {
      toast.error('Errore aggiornamento posizione');
    }
  };

  // Apri mappa per aggiungere posizione a un luogo esistente
  const openMapPickerForPlace = (placeId) => {
    setEditingPlaceId(placeId);
    setShowMapPicker(true);
  };

  // Submit nuovo luogo
  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Inserisci il nome del luogo');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    if (!form.photo_url) {
      toast.error('Aggiungi una foto del luogo');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/places`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Luogo aggiunto!');
      setShowForm(false);
      setForm({
        name: '', description: '', place_type: 'spiaggia', difficulty: 'facile',
        has_parking: false, has_bathroom: false, has_food: false, phone: '',
        photo_url: null, location: null
      });
      setPhotoPreview(null);
      fetchPlaces();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  // Aggiungi posizione a un luogo esistente
  const addLocationToPlace = async (placeId) => {
    if (!navigator.geolocation) {
      toast.error('GPS non disponibile');
      return;
    }
    
    setUpdatingLocation(placeId);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.put(
            `${BACKEND_URL}/api/places/${placeId}/location`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Posizione aggiunta!');
          fetchPlaces();
        } catch (error) {
          toast.error('Errore aggiornamento posizione');
        } finally {
          setUpdatingLocation(null);
        }
      },
      () => {
        setUpdatingLocation(null);
        toast.error('Impossibile ottenere posizione');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Like/Unlike
  const toggleLike = async (placeId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/places/${placeId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlaces(prev => prev.map(p => 
        p.id === placeId 
          ? { ...p, likes: res.data.likes, is_liked: res.data.action === 'added' }
          : p
      ));
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Apri navigatore
  const openNavigator = (place) => {
    if (!place.has_location) {
      toast.error('Posizione non disponibile');
      return;
    }
    
    const { lat, lng } = place.location;
    const label = encodeURIComponent(place.name);
    
    // Rileva il sistema operativo
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    let url;
    if (isIOS) {
      // Apple Maps
      url = `maps://maps.apple.com/?daddr=${lat},${lng}&q=${label}`;
    } else if (isAndroid) {
      // Google Maps su Android
      url = `google.navigation:q=${lat},${lng}`;
    } else {
      // Fallback Google Maps web
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    
    // Prova ad aprire l'app nativa, altrimenti fallback a Google Maps web
    const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    
    const newWindow = window.open(url, '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.open(fallbackUrl, '_blank');
    }
  };

  // Elimina luogo
  const deletePlace = async (placeId) => {
    if (!window.confirm('Eliminare questo luogo?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/places/${placeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Luogo eliminato');
      setPlaces(prev => prev.filter(p => p.id !== placeId));
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Filtra luoghi
  const filteredPlaces = places.filter(p => {
    if (filter === 'all') return true;
    if (filter === 'mine') return p.is_mine;
    return p.place_type === filter;
  });

  const getPlaceTypeInfo = (typeId) => PLACE_TYPES.find(t => t.id === typeId) || PLACE_TYPES[5];
  const getDifficultyInfo = (diffId) => DIFFICULTY_LEVELS.find(d => d.id === diffId) || DIFFICULTY_LEVELS[0];

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Modal Selettore Mappa */}
      {showMapPicker && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <MapIcon className="w-5 h-5 text-emerald-400" />
                  Seleziona posizione
                </h3>
                <button
                  onClick={() => { setShowMapPicker(false); setEditingPlaceId(null); setSearchQuery(''); }}
                  className="p-1 text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Barra di ricerca */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchAddress()}
                    placeholder="Cerca indirizzo o località..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                  />
                </div>
                <button
                  onClick={searchAddress}
                  disabled={searching}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {searching ? '...' : 'Cerca'}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Cerca un indirizzo oppure tocca direttamente sulla mappa per posizionare il pin
              </p>
            </div>
            
            {/* Mappa */}
            <div className="h-[300px]">
              <APIProvider apiKey={MAPS_API_KEY}>
                <Map
                  center={mapPickerLocation}
                  zoom={14}
                  gestureHandling="greedy"
                  disableDefaultUI={true}
                  zoomControl={true}
                  mapId="place-picker-map"
                  onClick={(e) => {
                    if (e.detail?.latLng) {
                      setMapPickerLocation({
                        lat: e.detail.latLng.lat,
                        lng: e.detail.latLng.lng
                      });
                    }
                  }}
                >
                  <AdvancedMarker position={mapPickerLocation}>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 border-3 border-white shadow-lg flex items-center justify-center animate-bounce">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                  </AdvancedMarker>
                </Map>
              </APIProvider>
            </div>
            
            {/* Coordinate selezionate */}
            <div className="p-4 border-t border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-400">
                  <span className="text-emerald-400 font-mono">
                    {mapPickerLocation.lat.toFixed(5)}, {mapPickerLocation.lng.toFixed(5)}
                  </span>
                </div>
              </div>
              <button
                onClick={confirmMapLocation}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Conferma questa posizione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup Introduttivo */}
      {showIntroPopup && (
        <div key={`intro-popup-${language}`} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-emerald-500/30 rounded-2xl max-w-md w-full p-6 shadow-2xl shadow-emerald-500/10">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed'}}>
                <TranslatedText text="LUOGHI DA SCOPRIRE" />
              </h3>
            </div>
            
            <div className="text-slate-300 text-sm space-y-3">
              <p>
                <TranslatedText text="Hai scoperto un luogo che toglie il fiato? Una spiaggia nascosta, un'oasi naturalistica o un angolo di costa dove la natura respira intatta? Condividilo con la Community." />
              </p>
              
              <p className="font-semibold text-white"><TranslatedText text="Cosa puoi fare qui:" /></p>
              
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span>📸</span>
                  <span><strong className="text-cyan-400"><TranslatedText text="Carica una foto:" /></strong> <TranslatedText text="Mostraci la luce e i colori di quel luogo." /></span>
                </li>
                <li className="flex items-start gap-2">
                  <span>📍</span>
                  <span><strong className="text-cyan-400"><TranslatedText text="Geolocalizzazione:" /></strong> <TranslatedText text="Aiutaci a inserire questo punto sulla nostra mappa della bellezza." /></span>
                </li>
                <li className="flex items-start gap-2">
                  <span>📝</span>
                  <span><strong className="text-cyan-400"><TranslatedText text="Raccontaci perché è speciale:" /></strong> <TranslatedText text="Spiega gli aspetti naturalistici che meritano di essere conosciuti e rispettati." /></span>
                </li>
              </ul>
              
              <p className="italic text-emerald-400/80 text-xs border-l-2 border-emerald-500 pl-3 mt-4">
                <TranslatedText text="Ricorda: Il vero Custode del mare visita questi luoghi senza lasciare traccia, portando con sé solo emozioni e lasciando solo impronte sulla sabbia." />
              </p>
              
              <p className="text-slate-400 text-xs mt-3">
                <TranslatedText text="Aiutami a mappare il respiro blu del Mediterraneo. La tua segnalazione diventerà una tappa della nostra guida collettiva alla natura." />
              </p>
            </div>
            
            <button
              onClick={() => setShowIntroPopup(false)}
              className="w-full mt-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white font-semibold rounded-xl transition-all"
            >
              <TranslatedText text="Ho capito, iniziamo!" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center shadow-lg">
          <MapPin className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white" style={{fontFamily: 'Barlow Condensed'}}>
          LUOGHI DA SCOPRIRE
        </h2>
        <p className="text-emerald-400 text-sm mt-1">
          Condividi i posti naturalistici più belli
        </p>
      </div>

      {/* Pulsante nuovo luogo */}
      <button
        onClick={() => setShowForm(!showForm)}
        className={`w-full mb-4 py-3 rounded-xl flex items-center justify-center gap-2 font-semibold transition-all ${
          showForm 
            ? 'bg-slate-700 text-slate-300' 
            : 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/30'
        }`}
      >
        {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        {showForm ? 'Chiudi' : 'Aggiungi un Luogo'}
      </button>

      {/* Form nuovo luogo */}
      {showForm && (
        <div className="bg-slate-900/80 border border-emerald-500/30 rounded-2xl p-5 mb-6 space-y-4">
          <h3 className="text-lg font-bold text-emerald-400">Nuovo Luogo</h3>
          
          {/* Nome */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Nome del luogo *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Es: Spiaggia delle Due Sorelle"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
            />
          </div>
          
          {/* Tipo luogo */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Tipo di luogo</label>
            <div className="grid grid-cols-3 gap-2">
              {PLACE_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, place_type: type.id }))}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                      form.place_type === type.id
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${form.place_type === type.id ? type.color : ''}`} />
                    <span className="text-xs">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Descrizione */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Descrizione *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Descrivi il luogo, come raggiungerlo, cosa lo rende speciale..."
              rows={3}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
            />
          </div>
          
          {/* Difficoltà */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Difficoltà per raggiungere</label>
            <div className="flex gap-2">
              {DIFFICULTY_LEVELS.map(diff => (
                <button
                  key={diff.id}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, difficulty: diff.id }))}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all ${
                    form.difficulty === diff.id
                      ? `${diff.color} text-white border-transparent`
                      : 'bg-slate-800 border-slate-700 text-slate-400'
                  }`}
                >
                  {diff.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Servizi */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Servizi disponibili</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, has_parking: !p.has_parking }))}
                className={`flex-1 py-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                  form.has_parking
                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                    : 'bg-slate-800 border-slate-700 text-slate-500'
                }`}
              >
                <Car className="w-5 h-5" />
                <span className="text-xs">Parcheggio</span>
              </button>
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, has_bathroom: !p.has_bathroom }))}
                className={`flex-1 py-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                  form.has_bathroom
                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                    : 'bg-slate-800 border-slate-700 text-slate-500'
                }`}
              >
                <Bath className="w-5 h-5" />
                <span className="text-xs">Bagni</span>
              </button>
              <button
                type="button"
                onClick={() => setForm(p => ({ ...p, has_food: !p.has_food }))}
                className={`flex-1 py-3 rounded-lg border flex flex-col items-center gap-1 transition-all ${
                  form.has_food
                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                    : 'bg-slate-800 border-slate-700 text-slate-500'
                }`}
              >
                <Coffee className="w-5 h-5" />
                <span className="text-xs">Bar/Ristoro</span>
              </button>
            </div>
          </div>
          
          {/* Telefono di contatto */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Telefono di contatto (opzionale)</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="Es: +39 333 1234567"
                className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Il tuo numero sarà visibile agli altri utenti
            </p>
          </div>
          
          {/* Foto */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Foto del luogo *</label>
            {photoPreview ? (
              <div className="relative">
                <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => { setPhotoPreview(null); setForm(p => ({ ...p, photo_url: null })); }}
                  className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={compressingPhoto}
                className="w-full py-8 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-all flex flex-col items-center gap-2"
              >
                {compressingPhoto ? (
                  <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-8 h-8" />
                )}
                <span>Tocca per aggiungere foto</span>
              </button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>
          
          {/* Posizione GPS */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Posizione GPS (opzionale)</label>
            {form.location ? (
              <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-400">
                  <MapPin className="w-5 h-5" />
                  <span className="text-sm font-mono text-xs">
                    {form.location.lat.toFixed(4)}, {form.location.lng.toFixed(4)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, location: null }))}
                  className="text-slate-400 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  className="w-full py-3 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 flex items-center justify-center gap-2"
                >
                  <Navigation className="w-5 h-5" />
                  Usa posizione attuale (GPS)
                </button>
                <button
                  type="button"
                  onClick={() => setShowMapPicker(true)}
                  className="w-full py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 hover:bg-emerald-500/20 flex items-center justify-center gap-2"
                >
                  <Search className="w-5 h-5" />
                  Cerca sulla mappa
                </button>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Puoi aggiungere la posizione anche dopo aver salvato
            </p>
          </div>
          
          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
            Pubblica Luogo
          </button>
        </div>
      )}

      {/* Filtri */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${
            filter === 'all' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'
          }`}
        >
          Tutti
        </button>
        <button
          onClick={() => setFilter('mine')}
          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${
            filter === 'mine' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'
          }`}
        >
          I miei
        </button>
        {PLACE_TYPES.slice(0, 4).map(type => (
          <button
            key={type.id}
            onClick={() => setFilter(type.id)}
            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${
              filter === type.id ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Lista luoghi */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-400">Caricamento...</p>
        </div>
      ) : filteredPlaces.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/60 border border-slate-700 rounded-2xl">
          <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessun luogo trovato</p>
          <p className="text-slate-500 text-sm">Sii il primo ad aggiungere un posto!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPlaces.map(place => {
            const typeInfo = getPlaceTypeInfo(place.place_type);
            const diffInfo = getDifficultyInfo(place.difficulty);
            const TypeIcon = typeInfo.icon;
            
            return (
              <div key={place.id} className="bg-slate-900/60 border border-slate-700 rounded-2xl overflow-hidden">
                {/* Foto */}
                {place.photo_url && (
                  <div className="relative h-48">
                    <img src={place.photo_url} alt={place.name} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${diffInfo.color} text-white`}>
                        {diffInfo.label}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium bg-slate-900/80 ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Contenuto */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">{place.name}</h3>
                      <p className="text-slate-400 text-sm flex items-center gap-1 mt-1">
                        <User className="w-3 h-3" />
                        {place.created_by}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Like */}
                      <button
                        onClick={() => toggleLike(place.id)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all ${
                          place.is_liked 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-slate-800 text-slate-400 hover:text-red-400'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${place.is_liked ? 'fill-current' : ''}`} />
                        <span className="text-sm">{place.likes || 0}</span>
                      </button>
                      
                      {/* Azioni owner */}
                      {place.is_mine && (
                        <>
                          {!place.has_location && (
                            <button
                              onClick={() => openMapPickerForPlace(place.id)}
                              className="flex items-center gap-1 px-2 py-1 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg text-xs font-medium"
                              title="Aggiungi posizione"
                            >
                              <Search className="w-3 h-3" />
                              <span>Posizione</span>
                            </button>
                          )}
                          <button
                            onClick={() => deletePlace(place.id)}
                            className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Servizi */}
                  <div className="flex gap-3 mt-3">
                    {place.has_parking && (
                      <span className="flex items-center gap-1 text-xs text-cyan-400">
                        <Car className="w-3 h-3" /> Parcheggio
                      </span>
                    )}
                    {place.has_bathroom && (
                      <span className="flex items-center gap-1 text-xs text-cyan-400">
                        <Bath className="w-3 h-3" /> Bagni
                      </span>
                    )}
                    {place.has_food && (
                      <span className="flex items-center gap-1 text-xs text-cyan-400">
                        <Coffee className="w-3 h-3" /> Ristoro
                      </span>
                    )}
                    {place.has_location && (
                      <span className="flex items-center gap-1 text-xs text-emerald-400">
                        <MapPin className="w-3 h-3" /> GPS
                      </span>
                    )}
                  </div>
                  
                  {/* Descrizione espandibile */}
                  <button
                    onClick={() => setExpandedPlace(expandedPlace === place.id ? null : place.id)}
                    className="w-full mt-3 text-left"
                  >
                    <p className={`text-slate-300 text-sm ${expandedPlace !== place.id ? 'line-clamp-2' : ''}`}>
                      {place.description}
                    </p>
                    {place.description.length > 100 && (
                      <span className="text-cyan-400 text-xs flex items-center gap-1 mt-1">
                        {expandedPlace === place.id ? 'Mostra meno' : 'Leggi tutto'}
                        {expandedPlace === place.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </span>
                    )}
                  </button>
                  
                  {/* Telefono */}
                  {place.phone && (
                    <a 
                      href={`tel:${place.phone}`}
                      className="flex items-center gap-2 mt-3 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-cyan-400 transition-all"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{place.phone}</span>
                    </a>
                  )}
                  
                  {/* Pulsante Portami qui */}
                  {place.has_location && (
                    <button
                      onClick={() => openNavigator(place)}
                      className="w-full mt-3 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
                      data-testid={`navigate-place-${place.id}`}
                    >
                      <Navigation className="w-5 h-5" />
                      Portami qui
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlacesSection;
